/**
 * 2D helpers for the side-view play plane. Gameplay lives in the XY plane of
 * the Three.js scene: +X is right, +Y is up, and the camera looks down -Z.
 * One world unit is one metre, which is also the size of one tile.
 */

export interface Vec2 {
  x: number;
  y: number;
}

export const v2 = (x = 0, y = 0): Vec2 => ({ x, y });

export function vAdd(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}
export function vSub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}
export function vScale(a: Vec2, s: number): Vec2 {
  return { x: a.x * s, y: a.y * s };
}
export function vDot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}
export function vLen(a: Vec2): number {
  // Not Math.hypot: several times slower, and these are hot (world generation, physics).
  return Math.sqrt(a.x * a.x + a.y * a.y);
}
export function vDist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}
export function vNorm(a: Vec2): Vec2 {
  const l = vLen(a);
  return l > 1e-9 ? vScale(a, 1 / l) : v2();
}
export function vLerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** Rotate counter-clockwise by `a` radians. */
export function vRotate(v: Vec2, a: number): Vec2 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: c * v.x - s * v.y, y: s * v.x + c * v.y };
}

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const TAU = Math.PI * 2;

/** Wrap to (-PI, PI]. */
export function wrapAngle(a: number): number {
  a = (((a + Math.PI) % TAU) + TAU) % TAU - Math.PI;
  return a === -Math.PI ? Math.PI : a;
}

/** Interpolate angles along the short way round. */
export function lerpAngle(a: number, b: number, t: number): number {
  return a + wrapAngle(b - a) * t;
}

/** Axis-aligned box in world metres. */
export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function boxesOverlap(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}
